import { useEffect, useRef } from 'react'
import { trackScrollDepth } from '../lib/analytics'

/**
 * Hook to track scroll depth milestones
 * Fires events at 25%, 50%, 75%, and 100% scroll depth
 * Each milestone only fires once per page load
 */
export function useScrollDepth() {
  const firedMilestones = useRef<Set<number>>(new Set())

  useEffect(() => {
    const milestones = [25, 50, 75, 100]

    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      
      if (docHeight <= 0) return
      
      const scrollPercent = Math.round((scrollTop / docHeight) * 100)

      milestones.forEach((milestone) => {
        if (scrollPercent >= milestone && !firedMilestones.current.has(milestone)) {
          firedMilestones.current.add(milestone)
          trackScrollDepth(milestone)
        }
      })
    }

    // Throttle scroll events for performance
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledScroll, { passive: true })
    
    // Check initial scroll position
    handleScroll()

    return () => {
      window.removeEventListener('scroll', throttledScroll)
    }
  }, [])
}

